"use client";

import { useCallback, useEffect, useSyncExternalStore, type ReactNode } from "react";
import Image from "next/image";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { SportsbookLogoMark } from "@/components/sportsbook-logos";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { sportsbookLogoUrl } from "@/lib/sportsbook-logo-url";
import { formatUsd } from "@/components/odds-screen/live/labels";
import { formatOdds } from "@/components/fantasy-props/model";
import { HoverTip, TapTip } from "@/components/odds/stake-tip";
import { venueLeftOut, whalesSide, WHALES_LOCKED_LABEL, type WhalesSpec } from "@/lib/whales";
import { useWhalesGate } from "@/hooks/use-whales-gate";
import { useCatalog } from "@/hooks/use-odds-live";
import type { OddsLiveClient } from "@/lib/odds-screen/live/client";
import { topicId, type EndReason, type TopicState, type WhalesLadder, type WhalesSide, type WhalesTopic } from "@/lib/odds-screen/live/types";
import { decimalToAmerican, formatDecimalOdds, type OddsFormat } from "@/utils/calculators/odds-utils";

export const WHALES_TITLE = "Whale $ on this side";

export const WHALES_TIP = "Whales: the worst price sharp money on the exchanges will take.";

const TABLE_TEXT = "space-y-1.5 text-xs leading-snug";

function price(odds: number, format: OddsFormat): string {
  return format === "american" ? formatOdds(decimalToAmerican(odds), format) : formatDecimalOdds(odds, format);
}

const ENDED: Partial<Record<EndReason, string>> = {
  row_removed: "This line just left the board.",
  event_started: "The game has started, so these orders are closed.",
  event_removed: "This game left the board.",
};

export function WhalesLogoMark({ size, className }: { size: number; className?: string }) {
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md", className)} style={{ width: size, height: size }} aria-hidden="true">
      <Image src={sportsbookLogoUrl("whales.png")} alt="" width={size} height={size} className="object-contain" style={{ width: size, height: size }} unoptimized />
    </span>
  );
}

const LOGO_TIP = "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

function WhalesTipLogo({ size, tap, className }: { size: number; tap: boolean; className?: string }) {
  const logo = <WhalesLogoMark size={size} className={className} />;
  if (tap) {
    return (
      <TapTip label="Whales" content={<p>{WHALES_TIP}</p>} className={cn("inline-flex", LOGO_TIP)} contentClassName="w-64 p-3 text-xs leading-snug">
        {logo}
      </TapTip>
    );
  }
  return (
    <HoverTip content={<p>{WHALES_TIP}</p>} className="max-w-64 text-xs leading-snug">
      <span className={cn("inline-flex", LOGO_TIP)} role="img" aria-label="Whales" tabIndex={0}>{logo}</span>
    </HoverTip>
  );
}

const LOCKED_TITLE = "Whales is a Diamond feature";
const LOCKED_PRICE = "+000";
const LOCKED_MONEY = "$0.0k";

function LockedTrigger({ onUnlock, className, children }: { onUnlock: () => void; className: string; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={WHALES_LOCKED_LABEL}
      title={LOCKED_TITLE}
      onClick={(event) => {
        event.stopPropagation();
        onUnlock();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      className={className}
    >
      {children}
    </button>
  );
}

function LockedWhales({ onUnlock, className, children }: { onUnlock: () => void; className?: string; children?: ReactNode }) {
  return (
    <LockedTrigger onUnlock={onUnlock} className={cn("flex min-h-9 flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-zinc-400 transition-colors hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", className)}>
      {children}
      <span aria-hidden="true" className="data-num select-none text-[13px] font-semibold italic leading-4 tabular-nums blur-[3px]">{LOCKED_PRICE}</span>
      <span aria-hidden="true" className="flex items-center gap-0.5 text-[10px] font-medium leading-none text-zinc-500">
        <Lock size={9} />
        <span className="data-num select-none tabular-nums blur-[2px]">{LOCKED_MONEY}</span>
      </span>
    </LockedTrigger>
  );
}

export function WhalesHead({ tap }: { tap: boolean }) {
  const gate = useWhalesGate();
  return (
    <div className="flex w-full justify-center py-0.5">
      {gate.kind === "unlocked" ? (
        <WhalesTipLogo size={32} tap={tap} className="rounded-lg border border-border" />
      ) : (
        <LockedTrigger onUnlock={() => gate.unlock("header")} className={cn("relative inline-flex", LOGO_TIP)}>
          <WhalesLogoMark size={32} className="rounded-lg border border-border" />
          <span aria-hidden="true" className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-surface-line bg-surface-overlay text-zinc-400">
            <Lock size={9} />
          </span>
        </LockedTrigger>
      )}
    </div>
  );
}

function useWhalesLadder(client: OddsLiveClient, topic: WhalesTopic): TopicState<WhalesLadder> {
  const id = topicId(topic);
  useEffect(() => client.retain(topic), [client, id]);
  const listen = useCallback((onChange: () => void) => client.listen(topic, onChange), [client, id]);
  const read = () => client.read(topic);
  return useSyncExternalStore(listen, read, read);
}

function WhalesTable({ client, spec, format }: { client: OddsLiveClient; spec: WhalesSpec; format: OddsFormat }) {
  const ladder = useWhalesLadder(client, spec.cell.topic);
  const leftOut = venueLeftOut(spec.cell.book, useCatalog(client).value?.books);
  const ended = ladder.phase === "ended" ? ladder.ended : null;
  const side = whalesSide(spec);
  const live = ended === null && side !== null ? ladder.value?.sides.find((entry) => entry.name === side.name) ?? null : null;
  const note = ended === null ? null : ENDED[ended] ?? null;
  return (
    <>
      <p className="font-semibold">{`Whale bets on ${spec.line ?? "this side"}`}</p>
      {ladder.value === null && ended === null
        ? <p className="text-zinc-400">Loading whale bets…</p>
        : live === null || live.orders.length === 0
          ? <p>No whale bets on this side right now.</p>
          : (
            <>
              <table className="data-num w-full border-separate border-spacing-x-2 tabular-nums [&_td:first-child]:pl-0 [&_th:first-child]:pl-0">
                <thead>
                  <tr className="text-left text-[10px] font-medium text-zinc-400">
                    <th scope="col">Book</th>
                    <th scope="col">Price they want</th>
                    <th scope="col">Money</th>
                  </tr>
                </thead>
                <tbody>
                  {live.orders.map((order, i) => (
                    <tr key={i} className={cn(order.book === leftOut && "text-zinc-500")}>
                      <td>
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <SportsbookLogoMark sportsbook={order.book} size={14} decorative />
                          {sportsbookIdToDisplay(order.book)}
                          {order.book === leftOut && <span className="text-[10px] font-medium">not in fair</span>}
                        </span>
                      </td>
                      <td className="font-semibold">{price(order.odds, format)}</td>
                      <td>{formatUsd(order.stake)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>{`${WHALES_TITLE}: ${formatUsd(live.total)} across all levels`}</p>
            </>
          )}
      {note !== null && <p className="text-zinc-400">{note}</p>}
    </>
  );
}

function spokenOf(side: WhalesSide, text: string): string {
  return `${WHALES_TITLE}: ${formatUsd(side.money)} wants ${text}`;
}

type WhalesCellProps = { spec: WhalesSpec | null; format: OddsFormat; tap?: boolean; className?: string; children?: ReactNode };

export function WhalesCell(props: WhalesCellProps) {
  const gate = useWhalesGate();
  return gate.kind === "unlocked"
    ? <LiveWhalesCell client={gate.client} {...props} />
    : <LockedWhales onUnlock={() => gate.unlock("cell")} className={props.className}>{props.children}</LockedWhales>;
}

function LiveWhalesCell({ client, spec, format, tap = false, className, children }: WhalesCellProps & { client: OddsLiveClient }) {
  const side = spec === null ? null : whalesSide(spec);
  const mismatch = spec !== null && side === null ? spec : null;
  const mismatchKey = mismatch === null ? null : `${topicId(mismatch.cell.topic)}|${mismatch.cell.side}`;
  useEffect(() => {
    if (mismatch === null) return;
    client.report("whales_side_mismatch", mismatch.cell.topic, { side: mismatch.cell.side, names: mismatch.cell.summary.map((entry) => entry.name) });
  }, [client, mismatchKey]);
  if (spec === null || side === null) {
    return <div className={cn("flex h-9 items-center", className)}>{children}<span className="sr-only">No whale summary for this row</span></div>;
  }
  if (side.odds === null) {
    return (
      <div className={cn("flex h-9 items-center justify-center text-[13px] text-zinc-600", className)}>
        {children}
        <span aria-hidden="true">–</span>
        <span className="sr-only">No whale bets on this side</span>
      </div>
    );
  }
  const text = price(side.odds, format);
  const body = (
    <>
      {children}
      <span className="data-num text-[13px] font-semibold italic leading-4 tabular-nums">{text}</span>
      <span className="data-num whitespace-nowrap text-[10px] font-medium leading-none tabular-nums text-zinc-500" aria-hidden="true">{formatUsd(side.money)}</span>
    </>
  );
  const cell = cn("flex flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", className);
  const table = <WhalesTable client={client} spec={spec} format={format} />;
  const spoken = spokenOf(side, text);
  if (tap) {
    return (
      <TapTip label={`${spoken}, see every order`} content={table} className={cell} contentClassName={cn("w-auto max-w-[min(22rem,calc(100vw-2rem))] p-3", TABLE_TEXT)}>
        {body}
      </TapTip>
    );
  }
  return (
    <HoverTip content={table} className={cn("max-w-[22rem]", TABLE_TEXT)}>
      <div className={cn("min-h-9", cell)} role="group" tabIndex={0} aria-label={spoken}>{body}</div>
    </HoverTip>
  );
}

export function WhalesCard({ side, opp, format, tap }: { side: WhalesSpec | null; opp: WhalesSpec | null; format: OddsFormat; tap: boolean }) {
  const gate = useWhalesGate();
  if (gate.kind === "locked") {
    return (
      <div className="relative flex shrink-0 flex-col items-center rounded-md px-2.5 py-2">
        <LockedWhales onUnlock={() => gate.unlock("cell")} className="w-16 gap-1.5 py-0">
          <span className="flex h-6 items-center"><WhalesLogoMark size={24} /></span>
        </LockedWhales>
      </div>
    );
  }
  const slot = (spec: WhalesSpec | null) => <WhalesCell spec={spec} format={format} tap={tap} className="h-8 min-h-0 w-16 gap-0.5 py-0 text-foreground/90" />;
  return (
    <div className="relative flex shrink-0 flex-col items-center gap-1.5 rounded-md px-2.5 py-2">
      <span className="flex h-6 items-center">
        <WhalesTipLogo size={24} tap={tap} />
      </span>
      <div className="flex flex-col gap-0.5">
        {slot(side)}
        {opp !== null && slot(opp)}
      </div>
    </div>
  );
}
